import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { PencilIcon, PlayIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { useEndpoint, useUpdateEndpoint } from '../hooks/useEndpoints';
import { useFlag } from '../hooks/useFeatureFlags';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useToast } from '../contexts/ToastContext';
import { EndpointOverviewTab } from '../components/endpoints/EndpointOverviewTab';
import { EndpointHistoryTab } from '../components/endpoints/EndpointHistoryTab';
import { EndpointTestTab } from '../components/endpoints/EndpointTestTab';
import { EndpointApiDocsTab } from '../components/endpoints/EndpointApiDocsTab';
import { ResponseModeToggle } from '../components/endpoints/ResponseModeToggle';
import {
  normalizeEndpointPathValue,
  validateEndpointPath,
} from '../components/endpoints/EndpointPathInput';
import { describeEndpointSaveError } from '../components/endpoints/endpointSaveError';
import { ResourceVersionControls } from '../components/versioning/ResourceVersionControls';
import { ReleasesView } from '../components/environments/ReleasesView';
import { formatRelativeTime } from '../utils/dateUtils';
import { endpointDisplayPath } from '../utils/endpointUrl';
import { endpointDisplayName } from '../utils/endpointLabel';
import type { Endpoint, UpdateEndpointInput } from '../types';

// Addressable hub tabs: the `?tab=` query param maps to a tab NAME so callers
// (Runner redirects, Home "Run" cards) can deep-link by name regardless of the
// underlying tab order. History was renamed to "Runs".
// `releases` (task 760, named `rollout` until task 799) is APPENDED
// deliberately: tabs are addressed by name, so a new one at the end deep-links
// by name without renumbering the existing hardcoded jumps to Test (index 2).
const TAB_NAMES = ['overview', 'runs', 'test', 'docs', 'releases'] as const;
type TabName = (typeof TAB_NAMES)[number];

/**
 * Retired tab names and where they now live.
 *
 * Permanent, not transitional: `?tab=rollout` is in every link and bookmark
 * saved since task 760 shipped it, and a deep link that silently drops the
 * reader on Overview is worse than no deep link at all. Same pattern
 * `ServiceDetailPage` uses for the names its own restructure retired.
 */
const TAB_ALIASES: Record<string, TabName> = {
  rollout: 'releases',
};

/** The canonical name for a `?tab=` value, or null when it names no tab. */
function resolveTabName(raw: string | null): TabName | null {
  if (!raw) return null;
  if ((TAB_NAMES as readonly string[]).includes(raw)) return raw as TabName;
  return TAB_ALIASES[raw] ?? null;
}

/**
 * The row position of a `?tab=` value among the tabs actually RENDERED (task
 * 1217), the `ServiceDetailPage` rule: a tab whose flag is off is not on the
 * row, so its name falls back to Overview exactly as an unknown name does.
 */
function tabNameToIndex(name: string | null, visible: readonly TabName[]): number {
  const resolved = resolveTabName(name);
  if (!resolved) return 0;
  const index = visible.indexOf(resolved);
  return index === -1 ? 0 : index;
}

export default function EndpointDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: endpoint, isLoading, error } = useEndpoint(id);
  const updateEndpoint = useUpdateEndpoint();
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  // Queued (async) endpoints are flag-gated (task 336). Flag off → the
  // sync/async toggle is replaced with a static Sync value.
  const queuedEnabled = useFlag('innovate.fe.endpoints.queued');
  // The Releases tab exists only behind its own lever (task 760); ReleasesView
  // additionally renders an explanatory empty state when the account has no
  // environments, so the tab is never a dead end. The flag keeps its
  // `…rollout` name after the label rename (task 799) — renaming a live flag
  // would touch the manifest, Unleash and the generated types for no behavior
  // change.
  const environmentsEnabled = useFlag('innovate.fe.environments.rollout');
  // The Runs and API Docs tabs each have a lever (task 1217).
  const runsTabEnabled = useFlag('innovate.fe.endpoints.runs-tab');
  const docsTabEnabled = useFlag('innovate.fe.endpoints.api-docs-tab');
  const visibleTabs = useMemo<readonly TabName[]>(() => {
    const enabled: Record<TabName, boolean> = {
      overview: true,
      runs: runsTabEnabled,
      test: true,
      docs: docsTabEnabled,
      releases: environmentsEnabled,
    };
    return TAB_NAMES.filter(name => enabled[name]);
  }, [runsTabEnabled, docsTabEnabled, environmentsEnabled]);
  const selectedTabIndex = tabNameToIndex(searchParams.get('tab'), visibleTabs);
  const [editedEndpoint, setEditedEndpoint] = useState<Partial<Endpoint>>({});

  // Sync the selected tab into the URL (?tab=overview|runs|test) so the hub is
  // addressable and arriving with a ?tab value selects the right tab.
  const setSelectedTabIndex = useCallback((index: number) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', visibleTabs[index] ?? 'overview');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams, visibleTabs]);

  /** Select a tab by name, whatever its position on the row (task 1217). */
  const selectTab = useCallback(
    (name: TabName) => setSelectedTabIndex(Math.max(0, visibleTabs.indexOf(name))),
    [setSelectedTabIndex, visibleTabs]
  );

  /**
   * Rewrite a retired tab name to its canonical one, in place.
   *
   * A redirect rather than a second render path: the alias already selects the
   * right tab (via `tabNameToIndex`), and this replaces the URL so the address
   * bar — and anything copied out of it — carries the new vocabulary. `replace`
   * keeps the alias out of history, so Back does not bounce through it.
   */
  const rawTab = searchParams.get('tab');
  const aliasTarget =
    rawTab && !(TAB_NAMES as readonly string[]).includes(rawTab)
      ? TAB_ALIASES[rawTab]
      : undefined;

  useEffect(() => {
    if (!aliasTarget) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', aliasTarget);
        return next;
      },
      { replace: true }
    );
  }, [aliasTarget, setSearchParams]);

  // Reset edited endpoint when endpoint data changes or editing mode changes
  useEffect(() => {
    if (endpoint && isEditing) {
      setEditedEndpoint(endpoint);
    }
  }, [endpoint, isEditing]);

  const handleFieldChange = (field: string, value: any) => {
    setEditedEndpoint(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Bulk edit runs the same path contract as the create form and the inline
    // editor (task 388): validate before sending, store the normalized value,
    // and surface whatever the server rejects instead of logging it.
    const pathValue = editedEndpoint.path ?? endpoint?.path ?? '';
    const pathError = validateEndpointPath(pathValue);
    if (pathError) {
      toast.error(pathError);
      return;
    }

    try {
      await updateEndpoint.mutateAsync({
        id: id!,
        data: {
          ...editedEndpoint,
          path: normalizeEndpointPathValue(pathValue),
        } as UpdateEndpointInput,
      });
      setIsEditing(false);
      toast.success('Endpoint updated successfully');
    } catch (error) {
      toast.error(describeEndpointSaveError(error, 'Failed to update endpoint').message);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedEndpoint({});
  };

  const handleTest = () => {
    selectTab('test');
  };

  const handleToggleResponseMode = async () => {
    try {
      await updateEndpoint.mutateAsync({
        id: id!,
        data: { responseQueued: !endpoint?.responseQueued }
      });
      toast.success(`Switched to ${endpoint?.responseQueued ? 'synchronous' : 'asynchronous'} mode`);
    } catch (error) {
      toast.error('Failed to toggle response mode');
      console.error('Failed to toggle response mode:', error);
    }
  };

  const handleEnterEditMode = () => {
    if (!isEditing) {
      setIsEditing(true);
      toast.info('Edit mode enabled');
    }
  };

  const handleExitEditMode = () => {
    if (isEditing) {
      setIsEditing(false);
      toast.info('Edit mode disabled');
    }
  };

  const handleSwitchToTestTab = () => {
    if (!isEditing) {
      selectTab('test');
      toast.info('Switched to Test tab');
    }
  };

  const handleCopyPath = async () => {
    if (!isEditing && endpoint) {
      // Copy the slug-qualified path — this is a "how you call it" affordance,
      // so it must be the real callable shape (task 385).
      const pathText = `${endpoint.method} ${endpointDisplayPath(endpoint)}`;
      try {
        await navigator.clipboard.writeText(pathText);
        toast.success('Path copied to clipboard', pathText);
      } catch (err) {
        toast.error('Failed to copy path');
      }
    }
  };

  const handleSaveShortcut = () => {
    if (isEditing) {
      handleSave();
    }
  };

  // Track if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!endpoint || !isEditing) return false;

    return (
      editedEndpoint.method !== endpoint.method ||
      editedEndpoint.path !== endpoint.path ||
      editedEndpoint.description !== endpoint.description ||
      editedEndpoint.responseQueued !== endpoint.responseQueued ||
      editedEndpoint.threadsEnabled !== endpoint.threadsEnabled ||
      editedEndpoint.requestFormat !== endpoint.requestFormat ||
      editedEndpoint.collaborationConfigId !== endpoint.collaborationConfigId ||
      JSON.stringify(editedEndpoint.input) !== JSON.stringify(endpoint.input) ||
      JSON.stringify(editedEndpoint.output) !== JSON.stringify(endpoint.output)
    );
  }, [editedEndpoint, endpoint, isEditing]);

  // Register keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'e',
        callback: handleEnterEditMode,
        description: 'Enter edit mode',
      },
      {
        key: 't',
        callback: handleSwitchToTestTab,
        description: 'Switch to Test tab',
      },
      {
        key: 'c',
        callback: handleCopyPath,
        description: 'Copy path to clipboard',
      },
      {
        key: 'escape',
        callback: handleExitEditMode,
        description: 'Exit edit mode',
      },
      {
        key: 's',
        ctrlKey: true,
        metaKey: true,
        callback: handleSaveShortcut,
        description: 'Save changes',
        preventDefault: true,
      },
    ],
    enabled: !!endpoint && !isLoading && !error,
    ignoreWhenTyping: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading endpoint...</div>
      </div>
    );
  }

  if (error || !endpoint) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading endpoint. Please try again.</div>
      </div>
    );
  }

  const actions = isEditing ? (
    <div className="flex gap-2">
      <button
        onClick={handleSave}
        disabled={updateEndpoint.isPending || !hasChanges}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <CheckIcon className="-ml-1 mr-2 h-4 w-4" />
        {updateEndpoint.isPending ? 'Saving...' : 'Save Changes'}
      </button>
      <button
        onClick={handleCancelEdit}
        disabled={updateEndpoint.isPending}
        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
      >
        <XMarkIcon className="-ml-1 mr-2 h-4 w-4" />
        Cancel
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-3">
      {queuedEnabled ? (
        <ResponseModeToggle
          isAsync={endpoint.responseQueued ?? false}
          onToggle={handleToggleResponseMode}
          disabled={updateEndpoint.isPending}
        />
      ) : (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          <span className="w-2 h-2 rounded-full mr-2 bg-purple-500" />
          Sync
        </span>
      )}
      <div className="flex gap-2">
        <button
          onClick={handleTest}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <PlayIcon className="-ml-1 mr-2 h-4 w-4" />
          Test
        </button>
        <button
          onClick={() => setIsEditing(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
          Edit
        </button>
      </div>
    </div>
  );

  // Format metadata subtitle (no description in header)
  const metadataSubtitle = `Endpoint #${endpoint.id.slice(0, 8)} · Created ${new Date(endpoint.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · Updated ${formatRelativeTime(endpoint.updatedAt)}`;

  return (
    <div>
      <PageHeader
        title={endpointDisplayName(endpoint)}
        description={metadataSubtitle}
        actions={actions}
      />

      {/* Version status + Publish (task 725). Deliberately below the header's
          action row rather than inside it: the status element is read-only, and
          mixing it in with buttons is exactly what makes people click it
          expecting something to happen. Renders no version status at all for
          accounts that have not adopted Publishing & versions (task 979). */}
      <ResourceVersionControls
        entityType="endpoint"
        entityId={endpoint.id}
        resourceName={endpointDisplayName(endpoint)}
        className="mt-3"
      />

      <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
        <Tab.List className="mt-6 flex space-x-1 border-b border-gray-200 dark:border-gray-700">
          <Tab
            className={({ selected }) =>
              `px-4 py-2 text-sm font-medium focus:outline-none ${
                selected
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`
            }
            disabled={isEditing}
          >
            Overview
          </Tab>
          {runsTabEnabled && (
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`
              }
              disabled={isEditing}
            >
              Runs
            </Tab>
          )}
          <Tab
            className={({ selected }) =>
              `px-4 py-2 text-sm font-medium focus:outline-none ${
                selected
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`
            }
            disabled={isEditing}
          >
            Test
          </Tab>
          {docsTabEnabled && (
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`
              }
              disabled={isEditing}
            >
              API Docs
            </Tab>
          )}
          {environmentsEnabled && (
            <Tab
              className={({ selected }) =>
                `px-4 py-2 text-sm font-medium focus:outline-none ${
                  selected
                    ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`
              }
              disabled={isEditing}
            >
              Releases
            </Tab>
          )}
        </Tab.List>
        <Tab.Panels className="mt-6">
          <Tab.Panel>
            <EndpointOverviewTab
              endpoint={isEditing ? { ...endpoint, ...editedEndpoint } : endpoint}
              isEditing={isEditing}
              onFieldChange={handleFieldChange}
            />
          </Tab.Panel>
          {runsTabEnabled && (
            <Tab.Panel>
              <EndpointHistoryTab endpointId={endpoint.id} onSwitchToTest={handleTest} />
            </Tab.Panel>
          )}
          <Tab.Panel>
            <EndpointTestTab endpoint={endpoint} />
          </Tab.Panel>
          {docsTabEnabled && (
            <Tab.Panel>
              <EndpointApiDocsTab endpoint={endpoint} />
            </Tab.Panel>
          )}
          {environmentsEnabled && (
            <Tab.Panel>
              <ReleasesView endpoint={endpoint} />
            </Tab.Panel>
          )}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
